import React, { useRef, useEffect, useState } from 'react';
import { MessageCircle, Phone, ArrowLeft, Users, Search, BookOpen, FileCheck } from 'lucide-react';
import { FaWhatsapp, FaWaze, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import ProcessSteps from '../components/ProcessSteps';
import ExpertiseAreas from '../components/ExpertiseAreas';
import ReasonsList from '../components/ReasonsCard';
import ReviewsSlider from '../components/Reviews';
import ContactButton from '../components/ContactButton';
import { Link } from 'react-router-dom';
// OptimizedImage component with mobile fix
const OptimizedImage = ({ src, alt, className, style, isMobile }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const placeholderColor = "#1e293b";

  useEffect(() => {
    const img = new Image();
    img.src = src;
    if (img.complete) setImageLoaded(true);
    else img.onload = () => setImageLoaded(true);
    return () => { img.onload = null; };
  }, [src]);

  return (
    <div className="relative w-full h-full overflow-hidden">
      <div
        className={`absolute inset-0 ${imageLoaded ? 'opacity-0' : 'opacity-100'} transition-opacity duration-700`}
        style={{ backgroundColor: placeholderColor }}
      />
      <img
        src={src}
        alt={alt}
        loading="eager"
        className={`${className} ${imageLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-700 ${
          isMobile ? 'w-full h-auto' : 'w-full h-full object-cover'
        }`}
        style={{
          ...style,
          ...(isMobile && { minHeight: '100%' })
        }}
      />
    </div>
  );
};

// Modern ContactButton with floating effect
const ModernContactButton = ({ href, icon: Icon, label, external = false }) => (
  <a 
    href={href}
    className="group relative bg-white/15 backdrop-blur-lg text-white 
               px-5 py-5 rounded-3xl font-medium shadow-lg shadow-black/20
               hover:bg-white/25 hover:shadow-xl hover:shadow-black/30 
               transition-all duration-500 flex items-center justify-center
               transform hover:scale-110 hover:-translate-y-2
               border border-white/30 hover:border-white/50
               min-w-[65px] md:min-w-[75px]"
    title={label}
  >
    <Icon className="w-6 h-6 md:w-7 md:h-7 text-white group-hover:scale-125 transition-all duration-500" />
    <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/10 via-transparent to-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
    <div className="absolute inset-0 rounded-3xl border-2 border-white/20 scale-100 group-hover:scale-125 group-hover:opacity-0 transition-all duration-500"></div>
  </a>
);

// Floating particles background


// Section wrapper with split layout - 40% image, 60% component
const SectionWithImage = ({ 
  children, 
  imageSrc, 
  imagePosition = "left",
  imageAlt = ""
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  if (isMobile) {
    // Mobile - just the component, no image
    return <div>{children}</div>;
  }

  // Desktop - split layout: 40% image, 60% component
  return (
    <div className="grid lg:grid-cols-5 min-h-screen">
      {/* Image Section - Takes 2/5 of the space (40%) */}
      <div className={`relative lg:col-span-2 ${imagePosition === 'right' ? 'order-2' : 'order-1'}`}>
        <img 
          src={imageSrc}
          alt={imageAlt}
          className="w-full h-full object-cover"
        />
        {/* Optional overlay for better text readability if needed */}
        <div className="absolute inset-0 bg-black/10"></div>
      </div>

      {/* Component Section - Takes 3/5 of the space (60%) */}
      <div className={`flex items-center justify-center bg-white lg:col-span-3 ${imagePosition === 'right' ? 'order-1' : 'order-2'}`}>
        <div className="w-full max-w-3xl px-8">
          {children}
        </div>
      </div>
    </div>
  );
};

const HomePage = () => {
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const expertiseRef = useRef(null);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });

    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };
    
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    const imagePath = '/אראלחןיושביםראשי.jpg';
    setBackgroundImage(imagePath);

    const preloadLink = document.createElement('link');
    preloadLink.rel = 'preload';
    preloadLink.as = 'image';
    preloadLink.href = imagePath;
    document.head.appendChild(preloadLink);

    return () => {
      window.removeEventListener("resize", checkScreenSize);
      if (preloadLink.parentNode) {
        preloadLink.remove();
      }
    };
  }, []);

  const renderHeroSection = () => {
    if (!backgroundImage) {
      return (
        <div className="relative h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 flex items-center justify-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-20 w-20 border-4 border-blue-400/30 border-t-blue-400"></div>
            <div className="absolute inset-2 animate-pulse rounded-full h-16 w-16 border-4 border-transparent border-b-blue-300/50"></div>
            <div className="absolute inset-4 animate-spin rounded-full h-12 w-12 border-2 border-transparent border-r-white/60" style={{ animationDirection: 'reverse' }}></div>
          </div>
        </div>
      );
    }

    if (isMobile) {
      return (
        <div className="relative h-auto overflow-hidden bg-slate-900">
          {/* תמונה עם הפתרון שעבד בעמוד הקשר */}
          <div className="relative w-full h-[40vh] overflow-hidden">
            <OptimizedImage 
              src={backgroundImage}
              alt="עו״ד אראל"
              className="w-full h-auto"
              style={{ 
                objectPosition: 'center center',
                minHeight: '100%'
              }}
              isMobile={true}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/80" />
          </div>
          
          {/* תוכן בחלק התחתון */}
          <div className="relative bg-gradient-to-b from-black/80 to-white h-[30vh] px-4 py-6 flex flex-col justify-center">
            <div className="text-white space-y-4 text-center">
              <h1 className="text-3xl font-bold">משרד עורכי דין</h1>
              <div className="flex items-center justify-center space-x-4 space-x-reverse">
                <div className="h-px bg-gradient-to-r from-blue-400 to-transparent flex-1 max-w-16"></div>
                <h2 className="text-lg font-light text-blue-200">מיכלסון דותן ושות</h2>
                <div className="h-px bg-gradient-to-l from-blue-400 to-transparent flex-1 max-w-16"></div>
              </div>
              <p className="text-sm bg-black/30 backdrop-blur-sm rounded-xl px-4 py-2 inline-block">
                מתמחים בחדלות פרעון והוצאה לפועל, נזיקין, אזרחי ונדל"ן
              </p>
              
              <div className="flex justify-center gap-3 pt-2">
                <ModernContactButton href="tel:0532788686" icon={Phone} label="טלפון" />
                <ModernContactButton href="https://wa.me/972532788686" icon={FaWhatsapp} label="WhatsApp" external />
                <ModernContactButton href="mailto:office@mdc-lawfirm.com" icon={FaEnvelope} label="אימייל" external />
                <ModernContactButton href="https://waze.com/ul?ll=31.9850714,34.7701406&navigate=yes" icon={FaWaze} label="Waze" external />
              </div>
            </div>
          </div>

         
        </div>
      );
    }

    // Desktop & Tablet Design
    return (
      <div className="relative h-screen overflow-hidden bg-slate-900">
        {/* Full screen background */}
        <div className="absolute inset-0">
          <OptimizedImage 
            src={backgroundImage}
            alt="עו״ד אראל"
            className="w-full h-full object-cover"
            style={{ objectPosition: isTablet ? 'center 40%' : 'center 30%' }}
            isMobile={false}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/40"></div>
        </div>

      
        {/* Content positioned asymmetrically */}
        <div className="relative h-full flex items-center z-10">
          <div className="w-full max-w-7xl mx-auto px-8 lg:px-16">
            <div className="grid lg:grid-cols-3 gap-12 items-center">
              {/* Main content */}
              <div className="lg:col-span-2 space-y-8">
                <div className="space-y-6">
                  <div className="space-y-4">
                    <h1 className="text-5xl xl:text-7xl font-bold text-white leading-tight">
                      משרד עורכי דין
                    </h1>
                    <div className="flex items-center space-x-4 space-x-reverse">
                      <div className="h-px bg-gradient-to-r from-blue-400 to-transparent flex-1"></div>
                      <h2 className="text-2xl xl:text-3xl font-light text-blue-200">
                        מיכלסון דותן ושות
                      </h2>
                      <div className="h-px bg-gradient-to-l from-blue-400 to-transparent flex-1"></div>
                    </div>
                  </div>
                  <p className="text-xl xl:text-2xl text-white/90 font-medium bg-black/30 backdrop-blur-sm rounded-2xl px-6 py-4 inline-block">
                    מתמחים בחדלות פרעון והוצאה לפועל, נזיקין, אזרחי ונדל"ן
                  </p>
                </div>
                <div className="flex flex-wrap gap-6 pt-8">
                  <ModernContactButton href="tel:0532788686" icon={Phone} label="טלפון" />
                  <ModernContactButton href="https://wa.me/972532788686" icon={FaWhatsapp} label="WhatsApp" external />
                  <ModernContactButton href="mailto:office@mdc-lawfirm.com" icon={FaEnvelope} label="אימייל" external />
                  <ModernContactButton href="https://waze.com/ul?ll=31.9850714,34.7701406&navigate=yes" icon={FaWaze} label="Waze" external />
                </div>
              </div>
              {/* Decorative side card */}
              <div className="hidden lg:block">
              <Link to="/contact" className="block">
                <div className="bg-white/10 rounded-3xl p-8 shadow-2xl shadow-black/30 border border-white/20 transform rotate-3 hover:rotate-0 transition-transform duration-700">
                  <div className="space-y-4 text-center">
                    <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto">
                      <Phone className="w-8 h-8 text-blue-300" />
                    </div>
                    <h3 className="text-lg font-semibold text-white">ייעוץ מקצועי</h3>
                    <p className="text-sm text-white/80">
                      קבלו ייעוץ ראשוני ללא התחייבות
                    </p>
                  </div>
                </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
      {renderHeroSection()}

      <div ref={expertiseRef}>
        <ExpertiseAreas />
      </div>
      
      {/* ProcessSteps with image on the left, component on the right */}
      <SectionWithImage 
      imageSrc="/אראל4.jpg" 
        imagePosition="left"
        imageAlt="תהליך משפטי"
      >
        <ProcessSteps />
      </SectionWithImage>
      <div className="h-16 md:h-24"></div> {/* רווח של 4rem/6rem */}
      {/* ReasonsList with image on the right, component on the left */}
      <SectionWithImage 
        imageSrc="/חן4.jpg"
        
        imagePosition="right"
        imageAlt="למה לבחור בנו"
      >
        <ReasonsList />
      </SectionWithImage>
  
      <div className="container mx-auto px-4 md:px-8 lg:px-12">
  <ReviewsSlider 
    showTitle={true}
    title="לקוחות ממליצים"
  />
</div>

      <div className="bg-gray-100 py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6 md:mb-8">קצת עלינו</h2>
          <p className="text-lg leading-relaxed text-gray-700">
            מיכלסון דותן ושות מתמחה במגוון תחומי המשפט האזרחי, עם דגש מיוחד על הוצאה לפועל וחדלות פירעון, דיני נזיקין ונדל"ן. 
            המשרד שם דגש על מתן שירות אישי ומקצועי, תוך שמירה על סטנדרטים גבוהים של מקצועיות ואתיקה. 
            אנו מאמינים כי כל לקוח ראוי ליחס אישי וליווי מקצועי צמוד, ופועלים ללא לאות להשגת התוצאות הטובות ביותר עבור לקוחותינו.
          </p>
        </div>
      </div>
      
      <ContactButton />
    </div>
  );
};

export default HomePage;